import React, { useState, useEffect } from 'react';

const ProductModal = ({ product, isOpen, onClose, onSave, categories, isUploading = false }) => {
  const [form, setForm] = useState({ 
    id: null, 
    name_bn: '', 
    name_en: '', 
    description_bn: '', 
    price: '', 
    offer_price: '', 
    category: categories && categories.length > 0 ? categories[0] : 'আচার', 
    image_url: '' 
  });
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    if (product) {
      setForm({
        id: product.id,
        name_bn: product.name_bn || '',
        name_en: product.name_en || '',
        description_bn: product.description_bn || '',
        price: product.price || '',
        offer_price: product.offer_price || '',
        category: product.category || (categories && categories.length > 0 ? categories[0] : 'আচার'),
        image_url: product.image_url || ''
      });
    } else {
      setForm({ 
        id: null, 
        name_bn: '', 
        name_en: '', 
        description_bn: '', 
        price: '', 
        offer_price: '', 
        category: categories && categories.length > 0 ? categories[0] : 'আচার', 
        image_url: '' 
      });
    }
    setImageFile(null);
  }, [product, isOpen, categories]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form, imageFile);
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4"
      onClick={onClose}
    >
      <div 
        className="bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 rounded-2xl shadow-2xl border border-slate-200 max-w-2xl w-full max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)'
        }}
      >
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-300">
            <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
              {product ? 'Edit Product' : 'Add New Product'}
            </h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-200 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Name (Bangla) *</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                  placeholder="রসুনের আচার"
                  value={form.name_bn}
                  onChange={(e) => setForm({ ...form, name_bn: e.target.value })}
                  required
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Name (English)</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                  placeholder="Garlic Pickle"
                  value={form.name_en}
                  onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Description (Bangla)</label>
                <textarea
                  rows="3"
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner resize-none"
                  placeholder="মায়ের হাতে তৈরি..."
                  value={form.description_bn}
                  onChange={(e) => setForm({ ...form, description_bn: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Price (৳) *</label>
                <input
                  type="number"
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                  placeholder="280"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Offer Price (৳)</label>
                <input
                  type="number"
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                  placeholder="250 (optional)"
                  value={form.offer_price}
                  onChange={(e) => setForm({ ...form, offer_price: e.target.value })}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Category *</label>
                <select
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  required
                >
                  {categories && categories.length > 0 ? (
                    categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))
                  ) : (
                    <option value="আচার">আচার</option>
                  )}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Image URL</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner"
                  placeholder="https://example.com/image.jpg"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Or Upload Image</label>
                <input
                  type="file"
                  accept="image/*"
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-inner file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-600 file:text-white hover:file:bg-slate-700"
                  onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                />
              </div>

              {form.image_url && (
                <div className="sm:col-span-2">
                  <img 
                    src={form.image_url} 
                    alt="Preview" 
                    className="w-full h-48 object-cover rounded-xl border-2 border-slate-300 shadow-inner"
                  />
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-300">
              <button
                type="submit"
                disabled={isUploading}
                className={`flex-1 px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-900 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden ${
                  isUploading ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                style={{
                  background: 'linear-gradient(135deg, #475569 0%, #1e293b 100%)',
                  boxShadow: '0 4px 15px rgba(30, 41, 59, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                }}
              >
                <div className="relative z-10 flex items-center justify-center gap-2">
                  {isUploading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Uploading Image...</span>
                    </>
                  ) : (
                    <span>{product ? 'Update Product' : 'Create Product'}</span>
                  )}
                </div>
                {isUploading && (
                  <div className="absolute bottom-0 left-0 h-1 bg-white/30 animate-pulse" style={{ width: '100%' }} />
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 bg-gradient-to-r from-slate-400 to-slate-500 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;

